/**
 * `routing.actions` — the result-screen handoff list (PR #865,
 * contracts/widget-config.md §Result actions). Up to four buttons, each one
 * routing mode with its own label and, where the mode needs one, a target.
 * An empty list keeps today's single CTA plus the app button; a non-empty
 * list replaces both.
 */
import { Field } from '../../ui/kit';
import { ACTION_MODES, ROUTING_ACTIONS_MAX, ROUTING_ACTION_LABEL_MAX, TRADES, type ActionMode, type RoutingAction } from '../model';
import { SettingsTabLink, useAppHandoff, usePath } from './common';

const MODE_LABEL: Record<ActionMode, string> = {
  partner_dispatch: 'Send to my team (contact form)',
  partner_booking: 'Book a visit',
  partner_call: 'Call',
  finderly_app: 'Open the FinderlyFix app',
};

const DEFAULT_LABEL: Record<ActionMode, string> = {
  partner_dispatch: 'Send to our team',
  partner_booking: 'Book a visit',
  partner_call: 'Call us',
  finderly_app: 'See the full guide in the app',
};

export function RoutingActionsSection() {
  const { value: actions, set, issues } = usePath<RoutingAction[]>('routing.actions');
  const app = useAppHandoff();
  const patch = (i: number, change: Partial<RoutingAction>) => set(actions.map((a, j) => (j === i ? { ...a, ...change } : a)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= actions.length) return;
    const next = actions.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    set(next);
  };
  const at = (i: number, key: string) => issues.filter((x) => x.path === `routing.actions.${i}.${key}`);
  const add = () => {
    const used = new Set(actions.map((a) => a.id));
    let n = actions.length + 1;
    while (used.has(`action_${n}`)) n += 1;
    set([...actions, { id: `action_${n}`, mode: 'partner_dispatch', label: DEFAULT_LABEL.partner_dispatch, phone: null, url: null, trades: [] }]);
  };
  const listIssues = issues.filter((x) => x.path === 'routing.actions');

  return (
    <fieldset className="stack">
      <legend className="visually-hidden">Result-screen actions</legend>
      <div className="row-between">
        <div>
          <div className="label">Result-screen actions</div>
          <p className="small muted" style={{ maxWidth: '60ch' }}>
            {actions.length
              ? 'These buttons replace the single onward action and the app button, in this order.'
              : 'None: the visitor sees the one action chosen above, plus the app button if it is on.'}
          </p>
        </div>
        <button type="button" className="btn btn-sm" onClick={add} disabled={actions.length >= ROUTING_ACTIONS_MAX}>
          Add action
        </button>
      </div>
      {listIssues.length ? <p className="err small" role="alert">{listIssues.map((x) => x.message).join(' ')}</p> : null}
      {actions.map((a, i) => (
        <div className="qcard" key={i} aria-label={`Action ${i + 1}`} role="group">
          <div className="row-between">
            <strong style={{ color: 'var(--ink)' }}>
              {i + 1}. {a.label || MODE_LABEL[a.mode]}
            </strong>
            <span className="row" style={{ gap: 4 }}>
              <button type="button" className="btn btn-ghost btn-sm" aria-label={`Move action ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                ↑
              </button>
              <button type="button" className="btn btn-ghost btn-sm" aria-label={`Move action ${i + 1} down`} disabled={i === actions.length - 1} onClick={() => move(i, 1)}>
                ↓
              </button>
              <button type="button" className="btn btn-sm btn-danger" onClick={() => set(actions.filter((_, j) => j !== i))}>
                Remove
              </button>
            </span>
          </div>
          <div className="grid-2">
            <Field label="Kind" issues={at(i, 'mode')}>
              {(p) => (
                <select
                  {...p}
                  className="input"
                  value={a.mode}
                  onChange={(e) => {
                    const mode = e.target.value as ActionMode;
                    // Targets belong to one kind only; drop the other kind's.
                    patch(i, {
                      mode,
                      phone: mode === 'partner_call' ? a.phone : null,
                      url: mode === 'partner_booking' ? a.url : null,
                      label: a.label === DEFAULT_LABEL[a.mode] ? DEFAULT_LABEL[mode] : a.label,
                    });
                  }}
                >
                  {ACTION_MODES.map((m) => (
                    <option key={m} value={m}>
                      {MODE_LABEL[m]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Button label" hint={`Up to ${ROUTING_ACTION_LABEL_MAX} characters.`} issues={at(i, 'label')}>
              {(p) => <input {...p} className="input" maxLength={ROUTING_ACTION_LABEL_MAX} value={a.label} onChange={(e) => patch(i, { label: e.target.value })} />}
            </Field>
          </div>
          {a.mode === 'finderly_app' && !app.enabled ? (
            <p className="small warn-ink">
              Hidden: the app handoff is off. <SettingsTabLink tab="handoff">Change in App handoff</SettingsTabLink>
            </p>
          ) : null}
          {a.mode === 'partner_call' ? (
            <Field label="Phone number" hint="International format. Empty dials the matched contractor from your roster." issues={at(i, 'phone')} optional>
              {(p) => (
                <input
                  {...p}
                  className="input input-mono"
                  inputMode="tel"
                  value={a.phone ?? ''}
                  placeholder="+15550102000"
                  onChange={(e) => patch(i, { phone: e.target.value.trim() === '' ? null : e.target.value.replace(/[\s()-]/g, '') })}
                />
              )}
            </Field>
          ) : null}
          {a.mode === 'partner_booking' ? (
            <Field label="Booking link" hint="https:// link to your booking flow. Empty shows the contact form instead." issues={at(i, 'url')} optional>
              {(p) => (
                <input
                  {...p}
                  className="input input-mono"
                  inputMode="url"
                  value={a.url ?? ''}
                  placeholder="https://book.example.com/repair"
                  onChange={(e) => patch(i, { url: e.target.value.trim() === '' ? null : e.target.value.trim() })}
                />
              )}
            </Field>
          ) : null}
          <div className="grid-2">
            <Field label="Reference id" hint="Reported in analytics and stored on the lead." issues={at(i, 'id')}>
              {(p) => <input {...p} className="input input-mono" value={a.id} onChange={(e) => patch(i, { id: e.target.value.trim() })} />}
            </Field>
            <div className="field">
              <span className="label" id={`trades-${i}`}>
                Show for
              </span>
              <div className="chips" role="group" aria-labelledby={`trades-${i}`}>
                <button type="button" className="chip" aria-pressed={a.trades.length === 0} onClick={() => patch(i, { trades: [] })}>
                  Every trade
                </button>
                {TRADES.map((t) => (
                  <button
                    key={t.slug}
                    type="button"
                    className="chip"
                    aria-pressed={a.trades.includes(t.slug)}
                    onClick={() =>
                      patch(i, { trades: a.trades.includes(t.slug) ? a.trades.filter((x) => x !== t.slug) : [...a.trades, t.slug] })
                    }
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {at(i, 'trades').length ? <p className="err small">{at(i, 'trades')[0]?.message}</p> : null}
            </div>
          </div>
        </div>
      ))}
    </fieldset>
  );
}
